import { useCallback, useEffect, useMemo, useState } from 'react'
import { CreditCard, Loader2 } from 'lucide-react'

import { ReportFilterBar } from '@/components/reports/ReportFilterBar'
import {
  exportGatewayTransactionReport,
  fetchGatewayTransactionReport,
  formatReportMoney,
  resolveReportPeriodBounds,
} from '@/lib/api/reports'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { useBranch } from '@/contexts/BranchContext'
import { useReportPanelFilters } from '@/contexts/ReportPreferencesContext'

export function GatewayTransactionReportPanel() {
  const { activeBranchId } = useBranch()
  const { period, setPeriod, compare, setCompare, customFrom, setCustomFrom, customTo, setCustomTo } =
    useReportPanelFilters()
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState('')

  const bounds = useMemo(() => {
    try {
      return resolveReportPeriodBounds(period, { from: customFrom, to: customTo })
    } catch {
      return null
    }
  }, [period, customFrom, customTo])

  const reportOptions = useMemo(
    () => ({
      period,
      from: period === 'custom' ? customFrom : bounds?.from,
      to: period === 'custom' ? customTo : bounds?.to,
      compare,
    }),
    [period, customFrom, customTo, bounds, compare],
  )

  const loadReport = useCallback(async () => {
    if (period === 'custom' && (!customFrom || !customTo)) {
      setReport(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError('')

    try {
      const data = await fetchGatewayTransactionReport(reportOptions)
      setReport(data)
    } catch (err) {
      setReport(null)
      setError(apiErrorMessage(err, 'Unable to load gateway transactions.'))
    } finally {
      setLoading(false)
    }
  }, [period, customFrom, customTo, reportOptions])

  useEffect(() => {
    loadReport()
  }, [loadReport, activeBranchId])

  const handleExport = async (format) => {
    setExporting(true)
    setError('')
    try {
      await exportGatewayTransactionReport(format, reportOptions)
    } catch (err) {
      setError(apiErrorMessage(err, `Unable to export ${format.toUpperCase()} report.`))
    } finally {
      setExporting(false)
    }
  }

  const summary = report?.summary ?? {}

  return (
    <div className="space-y-4">
      <ReportFilterBar
        period={period}
        onPeriodChange={setPeriod}
        compare={compare}
        onCompareChange={setCompare}
        customFrom={customFrom}
        customTo={customTo}
        onCustomFromChange={setCustomFrom}
        onCustomToChange={setCustomTo}
        onExportCsv={() => handleExport('csv')}
        onExportPdf={() => handleExport('pdf')}
        exporting={exporting}
        showCompare={false}
      />

      {error ? (
        <p className="rounded-xl border border-red-200/80 bg-red-50/80 px-3 py-2 text-sm text-red-700">{error}</p>
      ) : null}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading gateway transactions…
        </div>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-4">
            <div className="rounded-xl border border-border/60 bg-card p-3">
              <p className="text-[10px] uppercase text-muted-foreground">Captured</p>
              <p className="text-lg font-bold">{formatReportMoney(summary.captured_total)}</p>
            </div>
            <div className="rounded-xl border border-border/60 bg-card p-3">
              <p className="text-[10px] uppercase text-muted-foreground">Transactions</p>
              <p className="text-lg font-bold">{summary.transaction_count ?? 0}</p>
            </div>
            <div className="rounded-xl border border-border/60 bg-card p-3">
              <p className="text-[10px] uppercase text-muted-foreground">Refunded</p>
              <p className="text-lg font-bold">{summary.refunded_count ?? 0}</p>
            </div>
            <div className="rounded-xl border border-border/60 bg-card p-3">
              <p className="text-[10px] uppercase text-muted-foreground">Test mode</p>
              <p className="text-lg font-bold">{summary.test_mode_count ?? 0}</p>
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-border/60 bg-card">
            <table className="min-w-full text-left text-xs">
              <thead className="border-b border-border/60 bg-muted/30 text-[10px] uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Reference</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Amount</th>
                  <th className="px-3 py-2">Sale</th>
                  <th className="px-3 py-2">Customer</th>
                </tr>
              </thead>
              <tbody>
                {(report?.transactions ?? []).map((row) => (
                  <tr key={row.id} className="border-b border-border/40">
                    <td className="px-3 py-2 whitespace-nowrap">{row.created_at?.slice(0, 16).replace('T', ' ')}</td>
                    <td className="px-3 py-2 font-mono">{row.gateway_reference}</td>
                    <td className="px-3 py-2 capitalize">{row.status}</td>
                    <td className="px-3 py-2">{formatReportMoney(row.amount)}</td>
                    <td className="px-3 py-2">{row.sale_number ?? '—'}</td>
                    <td className="px-3 py-2">{row.customer_name ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(report?.transactions ?? []).length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-muted-foreground">
                <CreditCard className="mx-auto mb-2 h-6 w-6 opacity-40" />
                No gateway transactions in this period.
              </p>
            ) : null}
          </div>
        </>
      )}
    </div>
  )
}
